import { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { GridColDef } from "@mui/x-data-grid";
import SendIcon from "@mui/icons-material/Send";
import { api } from "../lib/api";
import { describeCheckError } from "../lib/check-error";
import { useAsync } from "../lib/hooks";
import { formatMs, newestFirst } from "../lib/format";
import { useI18n, type Translate } from "../lib/i18n";
import { COUNTRIES, COUNTRY_META, getCountry, type Country } from "../lib/country";
import type { CheckSource, Device, LinkCheckResult } from "../lib/types";
import { Badge, Button, Card, Empty, ErrorBox, Loading, Modal } from "../components/ui";
import { DataTable } from "../components/DataTable";

export function DeviceChecks({
  params,
  role,
}: {
  params: URLSearchParams;
  /** Vai trò của người đang đăng nhập (`User.role`) — quyết định có nút xoá lịch sử. */
  role: string;
}) {
  const { t, fmtDate, fmtAgo } = useI18n();
  const [tab, setTab] = useState<"compare" | "history">("compare");
  // Xoá lịch sử là thao tác toàn hệ thống (mọi brand) — backend chặn bằng
  // SuperadminGuard, UI ẩn hẳn nút với role khác cho khớp.
  const canClear = role === "superadmin";
  const [clearing, setClearing] = useState(false);
  const [sendingAlert, setSendingAlert] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [linkId, setLinkId] = useState<string>(params.get("linkId") ?? "");
  const [deviceId, setDeviceId] = useState<string>(params.get("deviceId") ?? "");
  const [region, setRegion] = useState("");
  const [limit, setLimit] = useState(100);
  // Tab so sánh phân trang phía server (limit/offset) — không lọc link thì số bản
  // ghi bằng số link × số device nên không tải hết một lượt được.
  const [compareLimit, setCompareLimit] = useState(100);
  const [compareOffset, setCompareOffset] = useState(0);

  const links = useAsync(() => api.brandLinks.list(), []);
  const devices = useAsync(() => api.devices.list(), []);

  const compare = useAsync(
    () =>
      api.deviceChecks.latest({
        linkId: linkId ? Number(linkId) : undefined,
        limit: compareLimit,
        offset: compareOffset,
      }),
    [linkId, compareLimit, compareOffset, tab],
  );

  // Đổi link lọc hay số bản ghi mỗi trang thì trang cũ không còn nghĩa gì.
  useEffect(() => {
    setCompareOffset(0);
  }, [linkId, compareLimit]);

  const history = useAsync(
    () =>
      api.deviceChecks.list({
        linkId: linkId ? Number(linkId) : undefined,
        deviceId: deviceId ? Number(deviceId) : undefined,
        region: region || undefined,
        limit,
      }),
    [linkId, deviceId, region, limit, tab],
  );

  const linkName = (id: number) => {
    const l = (links.data ?? []).find(x => x.id === id);
    return l ? `${l.brand} · ${l.domain}` : `#${id}`;
  };

  const historyColumns = useMemo<GridColDef<LinkCheckResult>[]>(
    () => [
      {
        field: "checkedAt",
        headerName: t("table.time"),
        flex: 0.8,
        minWidth: 125,
        renderCell: ({ row }) => (
          <Tooltip title={fmtDate(row.checkedAt)}>
            <span>{fmtAgo(row.checkedAt)}</span>
          </Tooltip>
        ),
      },
      {
        field: "brandLinkId",
        headerName: t("checks.link"),
        flex: 1.5,
        minWidth: 170,
        valueGetter: (v: number) => linkName(v),
      },
      {
        field: "source",
        headerName: t("table.source"),
        flex: 0.6,
        minWidth: 100,
        renderCell: ({ row }) => <SourceChip source={row.source} t={t} />,
      },
      {
        field: "deviceName",
        headerName: t("table.device"),
        flex: 0.8,
        minWidth: 120,
        valueGetter: (v: string | null) => v ?? "—",
      },
      {
        field: "region",
        headerName: t("table.region"),
        flex: 0.7,
        minWidth: 100,
        valueGetter: (v: string | null) => v ?? "—",
      },
      {
        field: "network",
        headerName: t("table.network"),
        flex: 0.5,
        minWidth: 90,
        valueGetter: (v: string | null) => v ?? "—",
      },
      {
        field: "ip",
        headerName: t("table.ip"),
        flex: 0.8,
        minWidth: 120,
        renderCell: ({ row }) => <IpCell ip={row.ip} userAgent={row.userAgent} />,
      },
      {
        field: "healthStatus",
        headerName: t("table.status"),
        flex: 0.6,
        minWidth: 105,
        renderCell: ({ row }) => <Badge status={row.healthStatus} />,
      },
      {
        field: "statusCode",
        headerName: t("table.http"),
        type: "number",
        flex: 0.4,
        minWidth: 80,
        valueFormatter: (v: number | null) => v ?? "—",
      },
      {
        field: "responseMs",
        headerName: t("table.response"),
        type: "number",
        flex: 0.5,
        minWidth: 95,
        valueFormatter: (v: number | null) => formatMs(v),
      },
      {
        field: "error",
        headerName: t("table.error"),
        flex: 1,
        minWidth: 110,
        valueGetter: (_v, row) =>
          describeCheckError(
            t,
            { code: row.errorCode, statusCode: row.statusCode, detail: row.errorDetail },
            row.error,
          ) ?? "—",
      },
      {
        field: "ip",
        headerName: t("table.checkerIp"),
        flex: 0.7,
        minWidth: 120,
        renderCell: ({ row }) => (
          <Tooltip title={t("table.checkerIpHint")}>
            <span className="mono">{row.ip ?? "—"}</span>
          </Tooltip>
        ),
      },
    ],
    [t, fmtDate, fmtAgo, links.data],
  );

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>{t("checks.title")}</h1>
          <p className="muted">{t("checks.subtitle")}</p>
        </div>
        <div className="row-actions">
          <Button onClick={() => setSendingAlert(true)}>
            <SendIcon sx={{ fontSize: 16, mr: 0.5 }} />
            {t("checks.sendAlert")}
          </Button>
          <Button onClick={() => (tab === "compare" ? compare.reload() : history.reload())}>
            {t("common.refresh")}
          </Button>
          {canClear && (
            <Button variant="danger" onClick={() => setClearing(true)}>
              {t("checks.clear")}
            </Button>
          )}
        </div>
      </header>

      {flash && (
        <Alert severity="success" onClose={() => setFlash(null)} sx={{ mb: 1.5 }}>
          {flash}
        </Alert>
      )}

      <div className="tabs">
        <button
          className={tab === "compare" ? "tab tab-active" : "tab"}
          onClick={() => setTab("compare")}
        >
          {t("checks.tabCompare")}
        </button>
        <button
          className={tab === "history" ? "tab tab-active" : "tab"}
          onClick={() => setTab("history")}
        >
          {t("checks.tabHistory")}
        </button>
      </div>

      <Card>
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{ flexWrap: "wrap", "& > *": { minWidth: 170, flex: "1 1 170px" } }}
        >
          <TextField
            select
            size="small"
            label={t("checks.link")}
            value={linkId}
            onChange={e => setLinkId(e.target.value)}
          >
            <MenuItem value="">{t("checks.allLinks")}</MenuItem>
            {(links.data ?? []).map(l => (
              <MenuItem key={l.id} value={String(l.id)}>
                {l.brand} · {l.domain}
              </MenuItem>
            ))}
          </TextField>

          {tab === "compare" && (
            <TextField
              select
              size="small"
              label={t("checks.limit")}
              value={String(compareLimit)}
              onChange={e => setCompareLimit(Number(e.target.value))}
            >
              {[50, 100, 200, 500].map(n => (
                <MenuItem key={n} value={String(n)}>
                  {n}
                </MenuItem>
              ))}
            </TextField>
          )}

          {tab === "history" && (
            <>
              <TextField
                select
                size="small"
                label={t("table.device")}
                value={deviceId}
                onChange={e => setDeviceId(e.target.value)}
              >
                <MenuItem value="">{t("checks.allDevices")}</MenuItem>
                {(devices.data ?? []).map(d => (
                  <MenuItem key={d.id} value={String(d.id)}>
                    {d.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label={t("table.region")}
                placeholder={t("checks.regionPlaceholder")}
                value={region}
                onChange={e => setRegion(e.target.value)}
              />
              <TextField
                select
                size="small"
                label={t("checks.limit")}
                value={String(limit)}
                onChange={e => setLimit(Number(e.target.value))}
              >
                {[50, 100, 200, 500].map(n => (
                  <MenuItem key={n} value={String(n)}>
                    {n}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
        </Stack>

        {tab === "compare" ? (
          compare.loading ? (
            <Loading />
          ) : compare.error ? (
            <ErrorBox message={compare.error} />
          ) : (
            <>
              <CompareView results={compare.data?.items ?? []} linkName={linkName} />
              <OffsetPager
                offset={compare.data?.offset ?? compareOffset}
                limit={compareLimit}
                count={compare.data?.items.length ?? 0}
                hasMore={compare.data?.hasMore ?? false}
                onChange={setCompareOffset}
              />
            </>
          )
        ) : history.loading ? (
          <Loading />
        ) : history.error ? (
          <ErrorBox message={history.error} />
        ) : (
          <DataTable
            rows={newestFirst(history.data ?? [], "checkedAt")}
            columns={historyColumns}
            showToolbar
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        )}
      </Card>

      {clearing && (
        <ClearHistoryModal
          onClose={() => setClearing(false)}
          onDone={deleted => {
            setClearing(false);
            setFlash(t("checks.clearDone", { n: deleted }));
            setCompareOffset(0);
            compare.reload();
            history.reload();
          }}
        />
      )}

      {sendingAlert && (
        <SendDeviceAlertModal
          devices={devices.data ?? []}
          onClose={() => setSendingAlert(false)}
          onDone={msg => {
            setSendingAlert(false);
            setFlash(msg);
          }}
        />
      )}
    </div>
  );
}

/** Hộp thoại chủ động quét và gửi log lỗi thiết bị ra Bot theo quốc gia. */
function SendDeviceAlertModal({
  devices,
  onClose,
  onDone,
}: {
  devices: Device[];
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const { t } = useI18n();
  const [country, setCountry] = useState<Country>(getCountry());
  const [hours, setHours] = useState(24);
  const [deviceId, setDeviceId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.deviceChecks.sendAlert({
        country,
        hours,
        deviceId: deviceId ? Number(deviceId) : undefined,
      });
      const detail =
        res.message || `${res.sentDevices} devices, ${res.totalBrokenLinks} broken links`;
      onDone(`${t("checks.sendAlertSuccess")} (${detail})`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t("checks.sendAlertTitle")}
      onClose={busy ? () => {} : onClose}
    >
      <form onSubmit={submit} className="form">
        {error && <ErrorBox message={error} />}
        <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }}>
          {t("checks.sendAlertSubtitle")}
        </Alert>

        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            size="small"
            label={t("checks.sendAlertCountry")}
            value={country}
            onChange={e => setCountry(e.target.value as Country)}
            required
          >
            {COUNTRIES.map(c => (
              <MenuItem key={c} value={c}>
                {COUNTRY_META[c].flag} {COUNTRY_META[c].label} ({c.toUpperCase()})
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label={t("checks.sendAlertHours")}
            value={String(hours)}
            onChange={e => setHours(Number(e.target.value))}
          >
            {[1, 3, 6, 12, 24, 48, 72].map(h => (
              <MenuItem key={h} value={String(h)}>
                {h}h
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label={t("checks.sendAlertDevice")}
            value={deviceId}
            onChange={e => setDeviceId(e.target.value)}
          >
            <MenuItem value="">{t("checks.allDevices")}</MenuItem>
            {devices.map(d => (
              <MenuItem key={d.id} value={String(d.id)}>
                {d.name} {d.region ? `(${d.region})` : ""}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <div className="form-actions" style={{ marginTop: 20 }}>
          <Button type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {t("checks.sendAlertBtn")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Từ khoá người dùng phải gõ để mở khoá nút xoá — khớp `?confirm=all` của API. */
const CLEAR_KEYWORD = "all";

/**
 * Xác nhận xoá toàn bộ lịch sử kiểm tra. Thao tác này **không hoàn tác được** và
 * quét mọi brand nên chỉ một hộp thoại "OK/Cancel" là quá nhẹ: bắt gõ đúng từ
 * khoá `all` (cũng chính là giá trị `confirm` mà API đòi) mới bật được nút xoá.
 */
function ClearHistoryModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (deleted: number) => void;
}) {
  const { t } = useI18n();
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const armed = word.trim().toLowerCase() === CLEAR_KEYWORD;

  async function submit() {
    if (!armed || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.deviceChecks.clearAll();
      onDone(res.deleted ?? 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t("checks.clearTitle")}
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button variant="danger" onClick={submit} disabled={!armed} loading={busy}>
            {t("checks.clearConfirmAction")}
          </Button>
        </>
      }
    >
      <Alert severity="error" variant="outlined">
        {t("checks.clearWarn")}
      </Alert>
      <Typography variant="body2" color="text.secondary">
        {t("checks.clearKeeps")}
      </Typography>
      <TextField
        size="small"
        autoFocus
        label={t("checks.clearPromptLabel", { word: CLEAR_KEYWORD })}
        placeholder={CLEAR_KEYWORD}
        value={word}
        onChange={e => setWord(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter") submit();
        }}
      />
      {error && <ErrorBox message={error} />}
    </Modal>
  );
}

/**
 * Thanh phân trang cho endpoint kiểu offset. Server không trả tổng số (chỉ `hasMore`)
 * nên chỉ đi tới/lui được, không nhảy tới trang cuối.
 *
 * Dính đáy khung (`position: sticky`) để đứng yên một chỗ — tab so sánh render số
 * bảng thay đổi theo dữ liệu, nếu thả trôi thì thanh này nhảy theo số dòng.
 */
function OffsetPager({
  offset,
  limit,
  count,
  hasMore,
  onChange,
}: {
  offset: number;
  limit: number;
  count: number;
  hasMore: boolean;
  onChange: (offset: number) => void;
}) {
  const { t } = useI18n();
  const label =
    count === 0 ? t("pager.empty") : t("pager.range", { from: offset + 1, to: offset + count });

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: "center",
        justifyContent: "flex-end",
        position: "sticky",
        bottom: 0,
        zIndex: 2,
        mt: 1.5,
        py: 1,
        bgcolor: "background.paper",
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
        {label}
      </Typography>
      <Button disabled={offset <= 0} onClick={() => onChange(Math.max(0, offset - limit))}>
        {t("pager.prev")}
      </Button>
      <Button disabled={!hasMore} onClick={() => onChange(offset + limit)}>
        {t("pager.next")}
      </Button>
    </Stack>
  );
}

/** Ô hiển thị IP thật của lần check, tooltip là User-Agent. Server không có IP → "—". */
export function IpCell({ ip, userAgent }: { ip: string | null; userAgent: string | null }) {
  if (!ip) return <span>—</span>;
  return (
    <Tooltip title={userAgent ?? ""}>
      <span className="mono">{ip}</span>
    </Tooltip>
  );
}

function SourceChip({ source, t }: { source: CheckSource; t: Translate }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      color={source === "server" ? "primary" : "default"}
      label={source === "server" ? t("checks.sourceServer") : t("checks.sourceDevice")}
    />
  );
}

/**
 * Gom kết quả mới nhất theo từng link để nhìn ra bất đồng giữa các agent —
 * ví dụ link chết ở Viettel/Hà Nội nhưng vẫn sống ở góc nhìn datacenter.
 */
function CompareView({
  results,
  linkName,
}: {
  results: LinkCheckResult[];
  linkName: (id: number) => string;
}) {
  const { t, fmtDate, fmtAgo } = useI18n();

  const groups = useMemo(() => {
    const map = new Map<number, LinkCheckResult[]>();
    for (const r of results) {
      const list = map.get(r.brandLinkId) ?? [];
      list.push(r);
      map.set(r.brandLinkId, list);
    }
    return [...map.entries()].map(([id, rows]) => {
      const statuses = new Set(rows.map(r => r.healthStatus));
      return { id, rows, conflict: statuses.size > 1 };
    });
  }, [results]);

  const columns = useMemo<GridColDef<LinkCheckResult>[]>(
    () => [
      {
        field: "source",
        headerName: t("table.source"),
        flex: 0.6,
        minWidth: 100,
        renderCell: ({ row }) => <SourceChip source={row.source} t={t} />,
      },
      {
        field: "deviceName",
        headerName: t("table.device"),
        flex: 0.9,
        minWidth: 120,
        valueGetter: (v: string | null) => v ?? "—",
      },
      {
        field: "region",
        headerName: t("table.region"),
        flex: 0.7,
        minWidth: 100,
        valueGetter: (v: string | null) => v ?? "—",
      },
      {
        field: "network",
        headerName: t("table.network"),
        flex: 0.5,
        minWidth: 85,
        valueGetter: (v: string | null) => v ?? "—",
      },
      {
        field: "ip",
        headerName: t("table.ip"),
        flex: 0.7,
        minWidth: 115,
        renderCell: ({ row }) => <IpCell ip={row.ip} userAgent={row.userAgent} />,
      },
      {
        field: "healthStatus",
        headerName: t("table.status"),
        flex: 0.6,
        minWidth: 105,
        renderCell: ({ row }) => <Badge status={row.healthStatus} />,
      },
      {
        field: "statusCode",
        headerName: t("table.http"),
        type: "number",
        flex: 0.4,
        minWidth: 80,
        valueFormatter: (v: number | null) => v ?? "—",
      },
      {
        field: "responseMs",
        headerName: t("table.response"),
        type: "number",
        flex: 0.5,
        minWidth: 95,
        valueFormatter: (v: number | null) => formatMs(v),
      },
      {
        field: "error",
        headerName: t("table.error"),
        flex: 0.9,
        minWidth: 100,
        valueGetter: (_v, row) =>
          describeCheckError(
            t,
            { code: row.errorCode, statusCode: row.statusCode, detail: row.errorDetail },
            row.error,
          ) ?? "—",
      },
      {
        field: "checkedAt",
        headerName: t("table.time"),
        flex: 0.7,
        minWidth: 115,
        renderCell: ({ row }) => (
          <Tooltip title={fmtDate(row.checkedAt)}>
            <span>{fmtAgo(row.checkedAt)}</span>
          </Tooltip>
        ),
      },
    ],
    [t, fmtDate, fmtAgo],
  );

  if (groups.length === 0) return <Empty message={t("checks.emptyCompare")} />;

  const conflicts = groups.filter(g => g.conflict).length;

  return (
    <>
      {conflicts > 0 && (
        <div className="alert alert-warn">{t("checks.conflictWarn", { n: conflicts })}</div>
      )}

      <Stack spacing={2}>
        {groups
          .sort((a, b) => Number(b.conflict) - Number(a.conflict))
          .map(g => (
            <div key={g.id} className={g.conflict ? "compare-group conflict" : "compare-group"}>
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ alignItems: "center", mb: 1, minWidth: 0 }}
              >
                <Typography variant="subtitle2" noWrap sx={{ minWidth: 0 }}>
                  {linkName(g.id)}
                </Typography>
                {g.conflict && (
                  <Chip size="small" color="warning" variant="outlined" label={t("checks.conflict")} />
                )}
              </Stack>
              <DataTable rows={g.rows} columns={columns} compactFooter />
            </div>
          ))}
      </Stack>
    </>
  );
}
